import { supabase } from '@/integrations/supabase/client';
import type { Database, Tables, TablesInsert, TablesUpdate } from '@/integrations/supabase/types';

// CRUD factory for creating standardized entity operations
import { applyFilters, applyOrdering, applyLimit } from './utils';

// Using proper Supabase types from the client

type SelectFields = string | string[] | undefined;

const normalizeSelectFields = (fields: SelectFields, defaultSelect: string): string => {
  if (!fields) return defaultSelect;
  if (Array.isArray(fields)) {
    return fields.join(', ');
  }
  return fields;
};

const normalizeListArgs = (
  input: string | { orderBy?: string; fields?: SelectFields } | undefined,
  defaultOrderBy: string,
  defaultSelect: string
): { orderBy: string; select: string } => {
  if (input === undefined || typeof input === 'string') {
    return {
      orderBy: input ?? defaultOrderBy,
      select: defaultSelect,
    };
  }

  // At this point, input must be the object type since string/undefined are handled above
  const inputObj = input as { orderBy?: string; fields?: SelectFields };
  return {
    orderBy: inputObj.orderBy ?? defaultOrderBy,
    select: normalizeSelectFields(inputObj.fields, defaultSelect),
  };
};

type TransformConfig<T = unknown> =
  | ((data: unknown) => unknown)
  | {
      transformToDB?: (data: unknown) => unknown;
      transformFromDB?: (data: unknown) => T;
    }
  | null
  | undefined;

const normalizeTransformers = <T>(
  transformConfig: TransformConfig<T>
): {
  toDB: ((data: unknown) => unknown) | null;
  fromDB: ((data: unknown) => T) | null;
} => {
  if (!transformConfig) {
    return { toDB: null, fromDB: null };
  }

  if (typeof transformConfig === 'function') {
    return { toDB: transformConfig, fromDB: null };
  }

  return {
    toDB: transformConfig.transformToDB ?? null,
    fromDB: transformConfig.transformFromDB ?? null,
  };
};

const applyFromDB = <T>(data: unknown, fromDB: ((data: unknown) => T) | null): T => {
  if (!fromDB || data === null || data === undefined) {
    return data as T;
  }

  if (Array.isArray(data)) {
    return data.map(item => fromDB(item)) as T;
  }

  return fromDB(data);
};

// Helper to ensure array return type
const applyFromDBArray = <T>(data: unknown[], fromDB: ((data: unknown) => T) | null): T[] => {
  if (!fromDB) {
    return data as T[];
  }

  return data.map(item => fromDB(item));
};

type CRUDEntity<
  T = unknown,
  TInsert = Record<string, unknown>,
  TUpdate = Record<string, unknown>,
  TId = string | number,
> = {
  list: (orderByOrOptions?: string | { orderBy?: string; fields?: SelectFields }) => Promise<T[]>;
  get: (id: TId) => Promise<T>;
  create: (recordData: TInsert) => Promise<T>;
  update: (id: TId, recordData: TUpdate) => Promise<T>;
  delete: (id: TId) => Promise<{ success: boolean }>;
  filter: (
    filters: Record<string, unknown>,
    orderBy?: string,
    limit?: number | null
  ) => Promise<T[]>;
} & Record<string, unknown>;

/**
 * Create a standard CRUD entity object
 * @param {string} tableName - Supabase table name
 * @param {Function} transformFn - Optional data transformation function
 * @param {Object} options - Additional options
 * @returns {Object} Entity CRUD operations
 */
export const createCRUDEntity = <
  TableName extends keyof Database['public']['Tables'],
  T = Tables<TableName>,
  TInsert = TablesInsert<TableName>,
  TUpdate = TablesUpdate<TableName>,
>(
  tableName: TableName,
  transformConfig: TransformConfig<T>,
  options: {
    orderField?: string;
    selectFields?: string;
    customMethods?: Record<string, unknown>;
  } = {}
): CRUDEntity<T, TInsert, TUpdate> => {
  const { orderField = 'created_at', selectFields = '*', customMethods = {} } = options;

  const { toDB: transformToDB, fromDB: transformFromDB } = normalizeTransformers(transformConfig);

  return {
    /**
     * List all records
     * @param {string|Object} orderByOrOptions - Order string or { orderBy, fields }
     * @returns {Promise<Array>} List of records
     */
    list: async (orderByOrOptions = `-${orderField}`) => {
      const { orderBy, select } = normalizeListArgs(
        orderByOrOptions,
        `-${orderField}`,
        selectFields
      );

      const isDescending = orderBy.startsWith('-');
      const orderByField = isDescending ? orderBy.slice(1) : orderBy;
      // Type assertion needed for dynamic table names
      const query = supabase.from(tableName);
      const { data, error } = await query.select(select).order(orderByField, {
        ascending: !isDescending,
      } as const);
      if (error) throw error;
      return applyFromDBArray<T>(data, transformFromDB);
    },

    /**
     * Get a single record by ID
     * @param {string|number} id - Record ID
     * @returns {Promise<Object>} Single record
     */
    get: async (id: string | number): Promise<T> => {
      const query = supabase.from(tableName);
      const { data, error } = await query
        .select(selectFields)
        .eq('id' as never, id as never)
        .single();
      if (error) throw error;
      return applyFromDB<T>(data, transformFromDB);
    },

    /**
     * Create a new record
     * @param {Object} recordData - Data to create
     * @returns {Promise<Object>} Created record
     */
    create: async (recordData: TInsert): Promise<T> => {
      const transformedData = transformToDB ? transformToDB(recordData) : recordData;

      // Check if user is authenticated
      const {
        data: { session },
      } = await supabase.auth.getSession();

      // If authenticated, use select to return created record
      // If anonymous, skip select to avoid RLS permission issues
      const table = supabase.from(tableName);

      if (session) {
        const result = await table
          .insert(transformedData as never)
          .select()
          .single();
        if (result.error) throw result.error;
        return applyFromDB<T>(result.data, transformFromDB);
      } else {
        const result = await table.insert(transformedData as never);
        if (result.error) throw result.error;
        return recordData as unknown as T;
      }
    },

    /**
     * Update an existing record
     * @param {string|number} id - Record ID
     * @param {Object} recordData - Data to update
     * @returns {Promise<Object>} Updated record
     */
    update: async (id: string | number, recordData: TUpdate): Promise<T> => {
      const transformedData = transformToDB ? transformToDB(recordData) : recordData;

      // Check if user is authenticated
      const {
        data: { session },
      } = await supabase.auth.getSession();

      // If authenticated, use select to return updated record
      // If anonymous, skip select to avoid RLS permission issues
      // Type assertion needed for dynamic table names
      const table = supabase.from(tableName);

      if (session) {
        const result = await table
          .update(transformedData as never)
          .eq('id' as never, id as never)
          .select()
          .single();
        if (result.error) throw result.error;
        return applyFromDB<T>(result.data, transformFromDB);
      } else {
        const result = await table.update(transformedData as never).eq('id' as never, id as never);
        if (result.error) throw result.error;
        return { id, ...recordData } as T;
      }
    },

    /**
     * Delete a record
     * @param {string|number} id - Record ID
     * @returns {Promise<Object>} Success response
     */
    delete: async (id: string | number): Promise<{ success: boolean }> => {
      // Type assertion needed for dynamic table names
      const { error } = await supabase
        .from(tableName)
        .delete()
        .eq('id' as never, id as never);
      if (error) throw error;
      return { success: true };
    },

    /**
     * Filter records with optional ordering and limit
     * @param {Object} filters - Filter criteria
     * @param {string} orderBy - Order by field
     * @param {number} limit - Limit results
     * @returns {Promise<Array>} Filtered records
     */
    filter: async (
      filters: Record<string, unknown>,
      orderBy: string = `-${orderField}`,
      limit: number | null = null
    ): Promise<T[]> => {
      // Type assertion needed for dynamic table names
      let query = supabase.from(tableName).select(selectFields);

      // Apply filters
      query = applyFilters(query, filters) as typeof query;

      // Apply ordering
      query = applyOrdering(query, orderBy) as typeof query;

      // Apply limit
      query = applyLimit(query, limit) as typeof query;

      const { data, error } = await query;
      if (error) {
        console.error(`🏗️ ${tableName}.filter error:`, error);
        throw error;
      }

      const result = applyFromDBArray<T>(data, transformFromDB);

      return result;
    },

    // Include any custom methods
    ...customMethods,
  };
};

/**
 * Create a simple CRUD entity without transformations
 * @param {string} tableName - Supabase table name,
 * @param {Object} options - Additional options,
 * @returns {Object} Entity CRUD operations
 */
export const createSimpleCRUDEntity = <
  TableName extends keyof Database['public']['Tables'],
  T = Tables<TableName>,
>(
  tableName: TableName,
  options: {
    orderField?: string;
    selectFields?: string;
    customMethods?: Record<string, unknown>;
  } = {}
): CRUDEntity<T, TablesInsert<TableName>, TablesUpdate<TableName>> => {
  return createCRUDEntity<TableName, T>(tableName, null, options);
};
